import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import type { DropdownItem } from "@/ui/Inputs/SystemDropDown";
import type { Department } from "../contexts/AuthProvider";
import type { NurseChanges } from "../contexts/HospitalDataProvider";
import { ALERT_CORAL, CATEGORICAL_DEPARTMENT, INK, SURFACE } from "../theme/tokens";
import { trackErrorEvent } from "./analytics";
import { canMergeBonus } from "./incentive";
import {
	AGENCY_DISPLAY,
	type BaseNurseShift,
	type BaseRequest,
	type BreakRulesConfig,
	type Day,
	type DayOff,
	type DayStats,
	Days,
	type DayTimeMapping,
	type DepartmentLocation,
	DepartmentNames,
	DepartmentType,
	type DisplayEmploymentType,
	EmploymentType,
	EmrType,
	type HospitalData,
	hppdTargetAverage,
	hppdTargetForDate,
	type IncentivePlan,
	NotificationType,
	type Nurse,
	NurseFullRole,
	NurseFullRoleShort,
	type NurseInShift,
	NurseRole,
	NurseTrainingStatus,
	type OpenShiftRequest,
	Preferences,
	type ReplaceShiftRequest,
	type RequestResponsesStatus,
	type ScheduleData,
	type Shift,
	type ShiftCounts,
	type ShiftRequest,
	ShiftType,
	type SpecificPreference,
	type StatsPerson,
	UserRole,
	UserView,
	WageType,
	type WeekData,
	WeeklyHoursCountingMethod,
	type WeeklyHoursDefinition,
} from "./models";
import { filterNursesForStats } from "./statsCalculations";
import { COUNT_ALL_TRAINING, countsTowardsPpd, type TrainingPpdCounting } from "./trainingPpdCounting";
import { isAgencyNurse } from "./warningDialogs/utils";

// Re-export isAgencyNurse for convenience
export { isAgencyNurse };

interface NurseHoursInfo {
	numOfNurses: number;
	sumOfHours: number;
}

export type NursesHoursData = { Total: NurseHoursInfo } & Record<NurseRole, NurseHoursInfo>;

type ProcessedType<T> = T extends Date
	? Date
	: T extends Array<infer U>
		? Array<ProcessedType<U>>
		: T extends object
			? { [K in keyof T]: ProcessedType<T[K]> }
			: T;

export const snakeToCamel = (str: string): string => str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

// This function transform a list of object from snake_case to camelCase & convert all the dates to Date object
export function processApiResponse<T>(data: T): ProcessedType<T> {
	const processValue = (value: any, key: string): any => {
		if (DateTime.isDateTime(value)) return value;
		if (Array.isArray(value)) return value.map((item) => processValue(item, key));
		if (value !== null && typeof value === "object") return processObject(value);
		return value;
	};

	const processObject = (obj: Record<string, any>): any => {
		const processedObj: Record<string, any> = {};
		Object.entries(obj).forEach(([key, value]) => {
			const camelKey = snakeToCamel(key);
			processedObj[camelKey] = processValue(value, camelKey);
		});
		return processedObj;
	};

	return processValue(data, "") as ProcessedType<T>;
}

export type RoleKey = NurseRole;

export type RoleFullKey = NurseFullRole;

export const roleKeys: RoleKey[] = Object.values(NurseRole);

export const nurseRoleDisplayName: Partial<Record<NurseRole, string>> = {
	[NurseRole.RLPN]: "R-LPN",
};

export const rolesMap: Record<NurseRole, NurseFullRole> = Object.fromEntries(
	Object.keys(NurseRole).map((key) => {
		const roleKey = key as keyof typeof NurseRole;
		return [NurseRole[roleKey], NurseFullRole[roleKey]];
	}),
) as Record<NurseRole, NurseFullRole>;

/**
 * The roles a department's staff can be filtered and priced by: the department's own roles, plus any role a
 * nurse in it has. In theory the department's roles are enough, but nurses can carry a role the department no
 * longer lists, and they must still show up.
 */
export const getDepartmentNurseTypes = (departmentRoles: NurseRole[], nurses: Pick<Nurse, "role">[]): NurseFullRole[] => {
	const nursesRoles = new Set(nurses.map((n) => n.role));
	return Array.from(new Set([...departmentRoles.map((r) => rolesMap[r]), ...nursesRoles]));
};

export const roleFullNames = roleKeys.map((r) => rolesMap[r]);

export const inverseRolesMap: Record<RoleFullKey, RoleKey> = Object.fromEntries(Object.entries(rolesMap).map(([key, value]) => [value, key])) as Record<
	RoleFullKey,
	RoleKey
>;

export function isOverstaffed(day: Day) {
	return day.staffNo < day.nurses.filter((nurse: NurseInShift) => !nurse.isPendingToDelete).length;
}

export function isUnderstaffed(day: Day) {
	return day.staffNo > day.nurses.filter((nurse: NurseInShift) => !nurse.isPendingToDelete).length;
}

export function isUnderstaffedSnf(nursesHoursData: NursesHoursData, census: number, totalTargetHours: number) {
	return nursesHoursData.Total.sumOfHours < Math.ceil(census * totalTargetHours);
}

export function isOverstaffedSnf(nursesHoursData: NursesHoursData, census: number, totalTargetHours: number) {
	return nursesHoursData.Total.sumOfHours - 12 > Math.ceil(census * totalTargetHours);
}

export function getShiftCensus(currWeek: WeekData, shiftNo: number, dayIndex: number): number {
	const day = currWeek.weeklyShifts[shiftNo].days[dayIndex];
	if (day == null || day.census_low == null || day.census_high == null) {
		return 0;
	}
	return Math.round((day.census_low + day.census_high) / 2);
}

export function getCensus(currWeek: WeekData, dayIndex: number) {
	const result = currWeek.weeklyShifts.reduce(
		(acc, shift, shiftNo) => {
			const day = shift.days[dayIndex];

			if (acc.hasOverride) {
				return acc;
			}

			if (day.census_override) {
				return {
					hasOverride: true,
					overrideValue: day.census_override.override_value,
					totalCensus: 0,
				};
			}

			return {
				hasOverride: false,
				overrideValue: 0,
				totalCensus: acc.totalCensus + getShiftCensus(currWeek, shiftNo, dayIndex),
			};
		},
		{ hasOverride: false, overrideValue: 0, totalCensus: 0 },
	);

	return result.hasOverride ? result.overrideValue : Math.round(result.totalCensus / currWeek.weeklyShifts.length);
}

export function getSnfWantedNursesHours(currWeek: WeekData, dayIndex: number, department: Department, census?: number) {
	const dayDate = currWeek.weeklyShifts[0]?.days[dayIndex]?.date;
	const totalTargetHours = dayDate ? hppdTargetForDate(department.hppd_target, dayDate.weekday) : hppdTargetAverage(department.hppd_target);
	if (!census) {
		census = getCensus(currWeek, dayIndex);
	}

	return census * totalTargetHours;
}

export function getNursesHoursData(currWeek: WeekData, dayIndex: number, rolesToInclude: NurseFullRole[], trainingPpdCounting: TrainingPpdCounting) {
	const nursesHoursData: NursesHoursData = {
		Total: { numOfNurses: 0, sumOfHours: 0 },
		...Object.fromEntries(Object.values(NurseRole).map((role) => [role, { numOfNurses: 0, sumOfHours: 0 }])),
	} as NursesHoursData;

	for (let shiftNo = 0; shiftNo < currWeek.weeklyShifts.length; shiftNo++) {
		const day = currWeek.weeklyShifts[shiftNo]?.days[dayIndex];
		for (const nurseInShift of day.nurses) {
			if (!nurseInShift.isPendingToDelete && rolesToInclude.includes(nurseInShift.role) && countsTowardsPpd(trainingPpdCounting, nurseInShift.trainingStatus)) {
				let hours = nurseInShift.endTime.diff(nurseInShift.startTime).as("hours");
				if (hours < 0) {
					hours = hours + 24;
				}
				nursesHoursData.Total.numOfNurses += 1;
				nursesHoursData.Total.sumOfHours += hours;

				const shortRole = NurseFullRoleShort[nurseInShift.role];
				if (shortRole && nursesHoursData[shortRole]) {
					nursesHoursData[shortRole].numOfNurses += 1;
					nursesHoursData[shortRole].sumOfHours += hours;
				}
			}
		}
	}
	return nursesHoursData;
}

export function precentageOfSnfStaff(currWeek: WeekData, nurses: Nurse[], dayIndex: number, department: Department, rolesToInclude: NurseFullRole[]) {
	// Availability isn't an HPPD/PPD figure, so it counts every shift whatever the department's training-shift switches say.
	const nursesHoursData = getNursesHoursData(currWeek, dayIndex, rolesToInclude, COUNT_ALL_TRAINING);
	const wantedNursesHours = getSnfWantedNursesHours(currWeek, dayIndex, department);

	return (nursesHoursData.Total.sumOfHours / wantedNursesHours) * 100;
}

export function isCNARegulationCompliant(nursesHoursData: NursesHoursData, census: number) {
	const totalHours = Object.values(NurseRole).reduce((sum, role) => sum + nursesHoursData[role].sumOfHours, 0);
	return totalHours >= Math.ceil(census * 3.48);
}

export function isNursesRegulationCompliant(nursesHoursData: NursesHoursData, census: number) {
	return nursesHoursData.RN.sumOfHours + nursesHoursData.LPN.sumOfHours + nursesHoursData.LVN.sumOfHours >= Math.ceil(census * 0.55);
}

export function generateDayTimeMapping(hospitalData: any, weekIndex: number): DayTimeMapping {
	const dayTimeMapping: DayTimeMapping = {};
	const timeTypes: string[] = [];

	for (const shift of hospitalData?.weeks[weekIndex]?.weeklyShifts || []) {
		if (shift.type && !timeTypes.includes(shift.type)) {
			timeTypes.push(shift.type);
		}
	}

	for (const day in Days) {
		if (Number.isNaN(Number(day)) && timeTypes.length > 0) {
			for (const timeType of timeTypes) {
				const key = `${day}_${timeType}`;
				const index = timeTypes.indexOf(timeType) + 1;
				dayTimeMapping[key] = Number(Days[day]) * timeTypes.length + index;
			}
		}
	}

	return dayTimeMapping;
}

export function getDayTimeNumber(day: DateTime, timeType: string, hospitalData: any, weekIndex: number): number {
	const dayOfWeek = day.weekday % 7;
	const dayKey = Days[dayOfWeek];
	const key = `${dayKey}_${timeType}`;
	const number = generateDayTimeMapping(hospitalData, weekIndex)[key];

	if (number === undefined) {
		return -1;
	}
	return number;
}

export function countNurseOccurrences(hospitalData: any, nurseId: number, weekIndex: number): number {
	let nurseCount = 0;

	for (const shift of hospitalData?.weeks[weekIndex]?.weeklyShifts || []) {
		for (const day of shift.days) {
			const nursesWithoutPendingToDelete = day.nurses.filter((nurse: any) => !nurse.isPendingToDelete);
			if (nursesWithoutPendingToDelete.some((nurse: any) => nurse.nurseId === nurseId)) {
				nurseCount++;
			}
		}
	}
	return nurseCount;
}

export const calculatePercantage = (week: WeekData, nurses: Nurse[], department: Department, isHospital: boolean) => {
	const shiftTypes = week.weeklyShifts.map((shift) => shift.type);
	const shiftTypeCounts: ShiftCounts = {
		atTarget: "0",
		understaffed: "0",
		overstaffed: "0",
		totalNumber: 0,
		atTargetNumber: 0,
		understaffedNumber: 0,
		overstaffedNumber: 0,
	};

	if (shiftTypes.length < 1) {
		return shiftTypeCounts;
	}

	const totalTypes = isHospital ? shiftTypes.length * 7 : 7;
	let atTarget = 0;
	let understaffed = 0;
	let overstaffed = 0;
	const nursesMap: Map<number, Nurse> = new Map(nurses.map((nurse) => [nurse.id, nurse]));

	if (isHospital) {
		for (let t = 0; t < shiftTypes.length; t++) {
			for (let i = 0; i < 7; i++) {
				const hStaffNo = week.weeklyShifts[t]?.days[i]?.staffNo;
				const dayShifts = week.weeklyShifts[t]?.days[i]?.nurses;

				// Skip nurses marked as pending to delete
				if (dayShifts) {
					const nCount = dayShifts.filter((nurse: NurseInShift) => !nurse.isPendingToDelete && nursesMap.get(nurse.nurseId)?.role !== NurseFullRole.CNA).length;

					if (hStaffNo === nCount) {
						atTarget = atTarget + 1;
					} else if (nCount < hStaffNo) {
						understaffed = understaffed + 1;
					} else if (nCount > hStaffNo) {
						overstaffed = overstaffed + 1;
					}
				}
			}
		}
	} else {
		for (let i = 0; i < 7; i++) {
			const precentage = Math.floor(precentageOfSnfStaff(week, nurses, i, department, roleFullNames));
			if (precentage === 100) {
				atTarget += 1;
			} else if (precentage < 100) {
				understaffed += 1;
			} else if (precentage > 100) {
				overstaffed += 1;
			}
		}
	}

	shiftTypeCounts.atTarget = `${Math.round((atTarget / totalTypes) * 100)}`;
	shiftTypeCounts.understaffed = `${Math.round((understaffed / totalTypes) * 100)}`;
	shiftTypeCounts.overstaffed = `${100 - (Math.round((atTarget / totalTypes) * 100) + Math.round((understaffed / totalTypes) * 100))}`;
	shiftTypeCounts.totalNumber = totalTypes;
	shiftTypeCounts.atTargetNumber = atTarget;
	shiftTypeCounts.understaffedNumber = understaffed;
	shiftTypeCounts.overstaffedNumber = totalTypes - atTarget - understaffed;
	return shiftTypeCounts;
};

export const checkIfNurseOverAllowed = (hospitalData: any, nurse: any, weekIndex: number) => {
	return countNurseOccurrences(hospitalData, nurse.id, weekIndex) > nurse.allowedShifts;
};

export const validatePassword = (password: string) => {
	const errors = [];
	let criteriaCount = 0;

	if (password.length < 8) {
		errors.push("password.error.length");
	}

	if (password.match(/[a-z]/)) {
		criteriaCount++;
	}

	if (password.match(/[A-Z]/)) {
		criteriaCount++;
	}

	if (password.match(/[0-9]/)) {
		criteriaCount++;
	}

	if (password.match(/[!@#$%^&*(),.?":{}|<>]/)) {
		criteriaCount++;
	}

	if (criteriaCount < 2) {
		errors.push("password.error.complexity");
	}

	if (errors.length > 0) {
		return { is_valid: false, errors: errors };
	}
	return { is_valid: true, errors: [] };
};

export const useMobile = () => {
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768);
		};

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	return { isMobile, windowWidth };
};

export const isTouchDevice = (): boolean => {
	return (
		"ontouchstart" in window ||
		navigator.maxTouchPoints > 0 ||
		// @ts-expect-error
		navigator.msMaxTouchPoints > 0
	);
};

export const formatNumber = (num: number, digits = 2) => {
	return Number.isInteger(num) ? num : num.toFixed(digits);
};

export const formatDateRange = (startDate: DateTime, endDate: DateTime) => {
	return `${startDate.toFormat("MMMM d")} - ${endDate.toFormat("MMMM d")}`;
};

// Module-level state (not a hook/context) so isHeadNurse/isBedSideNurse can change meaning
// without editing their ~47 call sites. Written once per userInfo load by AuthProvider.
let currentView: UserView | undefined;

// Install the current view. Called only by AuthProvider; undefined falls back to the role alone.
export const setCurrentViewMode = (view: UserView | undefined): void => {
	currentView = view;
};

export const isBedSideNurse = (user_role: string) => {
	return user_role === UserRole.BedSideNurse && currentView !== UserView.admin;
};

// Checks `!== staff` (not `=== admin`) so a missing view never removes access, keeping existing
// admins working.
export const isHeadNurse = (user_role: string) => {
	return user_role === UserRole.HeadNurse && currentView !== UserView.staff;
};

// Which departments the picker can show. Switching sends the current view, so a department the user
// is not an admin in would fail. No view yet means an old token — show everything, as before.
export function departmentsForCurrentView<T extends { department_id: string }>(
	departments: T[],
	view: UserView | undefined,
	adminDepartmentIds: string[] | undefined,
	staffDepartmentIds: string[] | undefined,
): T[];
export function departmentsForCurrentView<T extends { department_id: string }>(
	departments: T[] | undefined,
	view: UserView | undefined,
	adminDepartmentIds: string[] | undefined,
	staffDepartmentIds: string[] | undefined,
): T[] | undefined;
export function departmentsForCurrentView<T extends { department_id: string }>(
	departments: T[] | undefined,
	view: UserView | undefined,
	adminDepartmentIds: string[] | undefined,
	staffDepartmentIds: string[] | undefined,
): T[] | undefined {
	if (!departments || !view) return departments;
	const allowed = (view === UserView.admin ? adminDepartmentIds : staffDepartmentIds) ?? [];
	return departments.filter((department) => allowed.includes(department.department_id));
}

// You need departments on both sides to switch. An admin with no nurse record anywhere has nothing
// to switch to, so don't offer it.
export const canSwitchBetweenViews = (adminDepartmentIds: string[] | undefined, staffDepartmentIds: string[] | undefined): boolean =>
	(adminDepartmentIds ?? []).length > 0 && (staffDepartmentIds ?? []).length > 0;

// Which department to open in the new view: the current one if it works there, otherwise the first
// that does.
export const resolveDepartmentIdForView = (
	targetView: UserView,
	currentDepartmentId: string | undefined,
	adminDepartmentIds: string[] | undefined,
	staffDepartmentIds: string[] | undefined,
): string | undefined => {
	const candidates = (targetView === UserView.admin ? adminDepartmentIds : staffDepartmentIds) ?? [];
	if (currentDepartmentId && candidates.includes(currentDepartmentId)) return currentDepartmentId;
	return candidates[0];
};

export function getWantedRoles(isLowry: boolean, rolesToDisplay: NurseFullRole[]): NurseFullRole[] {
	return isLowry ? roleFullNames : rolesToDisplay;
}

export function getChipColor(stats: DayStats | undefined, department: Department) {
	if (!stats) {
		return { backgroundColor: ALERT_CORAL, color: INK.SECONDARY };
	}
	return getChipColorForPercentage(Math.round((stats.totalHours / stats.wantedHours) * 100), department);
}

/** The day chip's colour for a staffing %, against the department's valid % range. */
export function getChipColorForPercentage(percentage: number, department: Pick<Department, "min_valid_hppd_percent" | "max_valid_hppd_percent">) {
	if (percentage > department.max_valid_hppd_percent || percentage < department.min_valid_hppd_percent) {
		return { backgroundColor: ALERT_CORAL, color: SURFACE };
	}

	return {
		backgroundColor: "var(--inhouse-primary-alpha-08)",
		color: INK.SECONDARY,
	};
}

export function getNurseChanges(
	hospitalData: ScheduleData,
	nurseID: number,
	weekIndexToCheck: number | undefined = undefined,
	timeFrameStart?: DateTime,
	timeFrameEnd?: DateTime,
): NurseChanges {
	const addedShifts: any[] = [];
	const removedShifts: any[] = [];
	const weeksToProcess = weekIndexToCheck !== undefined ? [hospitalData.weeks[weekIndexToCheck]] : hospitalData.weeks;

	weeksToProcess.forEach((week, relativeWeekIndex) => {
		const weekIndex = weekIndexToCheck ?? relativeWeekIndex;
		week.weeklyShifts.forEach((shift, shiftIndex) => {
			shift.days.forEach((day, dayIndex) => {
				const nurse = day.nurses.find((n) => n.nurseId === nurseID);
				if (
					nurse &&
					timeFrameStart &&
					timeFrameEnd &&
					(nurse.startTime < timeFrameStart || nurse.startTime > timeFrameEnd || nurse.endTime < timeFrameStart || nurse.endTime > timeFrameEnd)
				) {
					return;
				}
				if (!nurse?.isPending) {
					return;
				}
				if (nurse?.isPendingToDelete) {
					removedShifts.push({
						week: weekIndex,
						shift: shiftIndex,
						day: dayIndex,
					});
				} else {
					addedShifts.push({
						week: weekIndex,
						shift: shiftIndex,
						day: dayIndex,
					});
				}
			});
		});
	});

	return { additions: addedShifts, removals: removedShifts };
}

export function toDisplayEmploymentType(employmentType: EmploymentType): DisplayEmploymentType {
	return employmentType === EmploymentType.PerDiem ? AGENCY_DISPLAY : employmentType;
}

export function fromDisplayEmploymentType(value: DisplayEmploymentType): EmploymentType {
	return value === AGENCY_DISPLAY ? EmploymentType.PerDiem : (value as EmploymentType);
}

/**
 * The translation key for each employment type, for a screen that prints one to the user (`t(EMPLOYMENT_TYPE_LABEL_KEY[type])`).
 * `getEmploymentTypeDisplayName` below is its English-only twin, kept for its existing callers. Per diem prints as
 * "Agency", as `toDisplayEmploymentType` names it.
 */
export const EMPLOYMENT_TYPE_LABEL_KEY: Record<EmploymentType, string> = {
	[EmploymentType.FullTime]: "employment.type.full.time",
	[EmploymentType.PartTime]: "employment.type.part.time",
	[EmploymentType.PerDiem]: "employment.type.agency",
	[EmploymentType.Travel]: "employment.type.travel",
	[EmploymentType.FloatPool]: "employment.type.float.pool",
	[EmploymentType.PRN]: "employment.type.prn",
	[EmploymentType.Unknown]: "unknown",
};

export function getEmploymentTypeDisplayName(employmentType: EmploymentType): string {
	switch (employmentType) {
		case EmploymentType.FullTime:
			return "Full Time";
		case EmploymentType.PartTime:
			return "Part Time";
		case EmploymentType.PerDiem:
			return toDisplayEmploymentType(employmentType);
		case EmploymentType.Travel:
			return "Travel";
		case EmploymentType.FloatPool:
			return "Float Pool";
		case EmploymentType.PRN:
			return "PRN";
		case EmploymentType.Unknown:
			return "Unknown";
		default:
			trackErrorEvent("Not known employment type", { employmentType });
			return "Unknown";
	}
}

/**
 * Highest-priority ACTIVE EMR for a department, or undefined when none is active (a disabled EMR
 * behaves as no EMR — census falls back to manual). Priority mirrors the backend resolver
 * (PCC > Yardi > Matrix 360 > Matrix > Reliable Health).
 */
export function resolveActiveEmrType(department: Pick<Department, "emrIntegrations">): EmrType | undefined {
	const active = (department.emrIntegrations ?? []).filter((e) => e.active);
	const priority = [EmrType.PCC, EmrType.Yardi, EmrType.Matrix360, EmrType.Matrix, EmrType.ReliableHealth];
	return priority.find((type) => active.some((e) => e.type === type));
}

const EMR_SOURCE_NAMES: Record<EmrType, string> = {
	[EmrType.PCC]: "PCC",
	[EmrType.Yardi]: "Yardi",
	[EmrType.Matrix]: "Matrix",
	[EmrType.Matrix360]: "Matrix",
	[EmrType.ReliableHealth]: "Reliable Health",
};

/**
 * Name of the external census source shown in the staffing/census UI.
 * Hospital demos show a generic "EMR data" (the vendor is fictional); real depts
 * name their active integration (PCC / Yardi / Matrix / Reliable Health), falling back to "EMR".
 */
export function getCensusSourceName(department: Department, isHospitalDemoView: boolean): string {
	if (isHospitalDemoView) {
		return "EMR data";
	}
	const emrType = resolveActiveEmrType(department);
	return emrType ? EMR_SOURCE_NAMES[emrType] : "EMR";
}

/**
 * Full "… Census" label for the census-source read-only field. Demo hospitals drop the
 * "Census" suffix (they show a bare "EMR data"); real depts read "<vendor> Census".
 */
export function getCensusSourceLabel(department: Department, isHospitalDemoView: boolean): string {
	const name = getCensusSourceName(department, isHospitalDemoView);
	return isHospitalDemoView ? name : `${name} Census`;
}

/**
 * Converts a weekday string to a day number (0-6, where 0 is Sunday)
 * Used for mapping master schedule weekday names to day numbers
 *
 * @param weekday The name of the weekday
 * @returns The day number (0-6) or undefined if the weekday is invalid
 */
export function getDayNumberFromWeekday(weekday: string): number | undefined {
	const weekdayMap: Record<string, number> = {
		Sunday: 0,
		Monday: 1,
		Tuesday: 2,
		Wednesday: 3,
		Thursday: 4,
		Friday: 5,
		Saturday: 6,
	};

	// Try exact case match first
	if (weekday in weekdayMap) {
		return weekdayMap[weekday];
	}

	// Try case-insensitive match (convert to lowercase for comparison)
	const lowerCaseWeekday = weekday.toLowerCase();
	for (const [key, value] of Object.entries(weekdayMap)) {
		if (key.toLowerCase() === lowerCaseWeekday) {
			return value;
		}
	}

	// Try matching with first 3 characters (e.g., "Sun" for Sunday)
	if (weekday.length >= 3) {
		const shortWeekday = weekday.substring(0, 3).toLowerCase();
		for (const [key, value] of Object.entries(weekdayMap)) {
			if (key.substring(0, 3).toLowerCase() === shortWeekday) {
				return value;
			}
		}
	}

	// Return undefined if no match found
	return undefined;
}

export function getDayNameByIndex(dayNo: number): string {
	const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

	// Handle potential invalid indices
	if (dayNo < 0 || dayNo > 6) {
		return "";
	}

	return days[dayNo];
}

export function getClientWeeklyHoursDefinition(client: string): WeeklyHoursDefinition {
	switch (client) {
		case DepartmentNames.CenterAtLowryMainDept: {
			return {
				method: WeeklyHoursCountingMethod.ShiftBased,
				startDay: Days.Sunday,
				startShiftType: ShiftType.Day,
				endDay: Days.Saturday,
				endShiftType: ShiftType.Night,
			};
		}
		default: {
			return {
				method: WeeklyHoursCountingMethod.ShiftBased,
				startDay: Days.Sunday,
				startShiftType: ShiftType.Day,
				endDay: Days.Saturday,
				endShiftType: ShiftType.Night,
			};
		}
	}
}

export const getWage = (nurseDetails: Nurse) => {
	if (nurseDetails.wageType === WageType.Annually) {
		return nurseDetails.wage ? nurseDetails.wage / (52 * 40) : 0;
	}
	return nurseDetails.wage || 0;
};

/** What decides whether one shift is in a `getTotalHours` total — every input but the ignore lists. */
export interface TotalHoursFilter {
	showActual: boolean | undefined;
	isSnf: boolean;
	rolesToDisplay: NurseFullRole[] | undefined;
	jobManagementEnabled: boolean;
	allNurses: StatsPerson[];
	trainingPpdCounting: TrainingPpdCounting;
	includePpdExcluded: boolean;
}

/**
 * Whether `getTotalHours` counts this shift (its ignore lists aside): the department's roles, no zero-length
 * actual, and the shared HPPD filter. Exported so a caller that needs the answer per shift asks the same question.
 */
export function isShiftInTotalHours(nurse: NurseInShift, filter: TotalHoursFilter): boolean {
	if (!(filter.isSnf || filter.rolesToDisplay?.includes(nurse.role))) return false;
	if (filter.showActual && nurse.startTime === nurse.endTime) return false;
	return filterNursesForStats(nurse, filter.jobManagementEnabled, filter.allNurses, filter.trainingPpdCounting, filter.includePpdExcluded);
}

/** The hours one shift adds to a `getTotalHours` total: its work hours after the department's break rules. */
export function totalHoursOfShift(
	departmentId: string,
	nurse: NurseInShift,
	breakRulesConfig: BreakRulesConfig[],
	jobManagementEnabled: boolean,
	unionByNurseId: Map<number, string>,
): number {
	return getShiftWorkHours(
		departmentId,
		nurse.endTime,
		nurse.startTime,
		breakRulesConfig,
		NurseRole[NurseFullRoleShort[nurse.role] as keyof typeof NurseRole],
		nurse.type,
		nurse.jobId ? [nurse.jobId] : [],
		jobManagementEnabled,
		unionByNurseId.get(nurse.nurseId) ?? "",
	);
}

export function getTotalHours(
	departmentId: string,
	weeklyShifts: Shift[],
	dayIndex: number,
	showActual: boolean | undefined,
	isSnf: boolean,
	rolesToDisplay: NurseFullRole[] | undefined,
	ignoreNurseIDs: number[] | undefined,
	ignoreShiftIds: string[] | undefined,
	breakRulesConfig: BreakRulesConfig[] = [],
	jobManagementEnabled: boolean = false,
	allNurses: StatsPerson[],
	trainingPpdCounting: TrainingPpdCounting,
	includePpdExcluded: boolean,
) {
	let totalStaff = 0;
	let totalHours = 0;
	const unionByNurseId = buildNurseUnionMap(allNurses);
	const filter: TotalHoursFilter = { showActual, isSnf, rolesToDisplay, jobManagementEnabled, allNurses, trainingPpdCounting, includePpdExcluded };
	weeklyShifts.forEach((shift) => {
		const nurses = showActual ? shift.days[dayIndex].nursesActual : shift.days[dayIndex].nurses;
		nurses
			?.filter((nurse: NurseInShift) => {
				if (ignoreNurseIDs?.includes(nurse.nurseId)) return false;
				if (ignoreShiftIds?.includes(nurse.id)) return false;
				return isShiftInTotalHours(nurse, filter);
			})
			?.forEach((nurse: NurseInShift) => {
				totalStaff++;
				totalHours += totalHoursOfShift(departmentId, nurse, breakRulesConfig, jobManagementEnabled, unionByNurseId);
			});
	});
	return { totalStaff, totalHours };
}

/**
 * The robo-call window's defaults, for a department that sets none: from a day before a shift starts until an hour
 * in. One place, so a control that NAMES the window ("available from 24h before…") names the one it is gated by.
 */
export const CALL_OFF_BYPASS_DEFAULT_HOURS = { BEFORE_SHIFT: 24, AFTER_START: 1 } as const;

/** The robo-call window a department runs: its admin settings, else `CALL_OFF_BYPASS_DEFAULT_HOURS`. */
export const callOffBypassHours = (chosenDepartment: Department): { beforeShift: number; afterStart: number } => ({
	// DEFAULT: a department that never set the window gets the product's standard one (not "no window").
	beforeShift: chosenDepartment.callOffBypassHoursBeforeShiftAdmin ?? CALL_OFF_BYPASS_DEFAULT_HOURS.BEFORE_SHIFT,
	afterStart: chosenDepartment.callOffBypassHoursAfterStartAdmin ?? CALL_OFF_BYPASS_DEFAULT_HOURS.AFTER_START,
});

/** Where a shift stands against the robo-call window (`callOffBypassHours`). */
export enum CallOffBypassWindow {
	/** Inside it: a call-off skips the dialog and a robo call can start now. */
	Open = "open",
	/** Before it: it opens the bypass hours before the shift starts. */
	NotYet = "not_yet",
	/** After it: the shift is past the bypass hours after its start, and the window will not open again. */
	Closed = "closed",
}

/**
 * The robo-call window for a shift: open from the department's bypass hours before the start until its bypass hours
 * after it, told apart on either side so a control can say "not yet" or "no longer". The one place the window's edges
 * are measured — `shouldBypassCallOffDialog` is this function's `Open`.
 */
export function callOffBypassWindowOf(date: DateTime, chosenDepartment: Department, timezone?: string): CallOffBypassWindow {
	// DEFAULT: without a timezone "now" is the device's clock and the start is read as given — how the callers that pass
	// none have always measured it. A difference in hours is the same in any zone, so the window's edges do not move.
	const now = timezone ? DateTime.local().setZone(timezone) : DateTime.now();
	const shiftStart = timezone ? date.setZone(timezone) : date;

	const hoursUntilShiftStart = shiftStart.diff(now, "hours").hours;
	const hoursSinceShiftStart = now.diff(shiftStart, "hours").hours;

	const { beforeShift: bypassHoursBeforeShift, afterStart: bypassHoursAfterStart } = callOffBypassHours(chosenDepartment);

	const isShiftInNext24Hours = hoursUntilShiftStart >= 0 && hoursUntilShiftStart <= bypassHoursBeforeShift;
	const isLessThanOneHourIntoShift = hoursSinceShiftStart >= 0 && hoursSinceShiftStart < bypassHoursAfterStart;

	if (isShiftInNext24Hours || isLessThanOneHourIntoShift) return CallOffBypassWindow.Open;
	return hoursSinceShiftStart >= bypassHoursAfterStart ? CallOffBypassWindow.Closed : CallOffBypassWindow.NotYet;
}

export function shouldBypassCallOffDialog(date: DateTime, chosenDepartment: Department, timezone?: string): boolean {
	return callOffBypassWindowOf(date, chosenDepartment, timezone) === CallOffBypassWindow.Open;
}

export interface BreakPlacement {
	offsetHours: number; // Clock-time offset from shift start
	durationMinutes: number; // Duration of this break segment in minutes
	isPaid: boolean; // A paid break stays in worked hours
}

/**
 * Compute break placements using the multi-break model.
 * Each applicable rule produces a separate break. break_limit is evaluated against
 * work hours with strict > check. Work hours exclude unpaid breaks (earlier ones and this
 * one) but include paid ones, so only unpaid breaks move a later break on the clock; a paid
 * break that lands on another break moves to just after it. Mirrors the backend's
 * `compute_break_from_config`.
 *
 * - "addition": appends a new break of literal duration `breakTime` minutes.
 * - "override": clears all previously placed breaks and places one new break of
 *   literal duration `breakTime` minutes.
 *
 * `breakTime` is always treated as the literal duration of THIS break in minutes.
 * Rules with `breakTime <= 0` are skipped.
 */
/**
 * Job-scope match for OT / hours-rate / break-rule configs.
 * Empty cfg job_ids = match all jobs. Otherwise, match if any of the nurse's job_ids
 * is in cfg.jobIds.
 */
export function jobIdsMatchConfig(nurseJobIds: string[] | undefined, cfgJobIds: string[] | undefined): boolean {
	if (!cfgJobIds || cfgJobIds.length === 0) return true;
	if (!nurseJobIds || nurseJobIds.length === 0) return false;
	const cfgSet = new Set(cfgJobIds);
	return nurseJobIds.some((j) => cfgSet.has(j));
}

/**
 * Union-scope match for OT / hours-rate / break-rule configs. The FE mirrors the backend break and
 * rate math in a second implementation, so this MUST stay behaviourally identical to
 * `union_matches_config` in `backend/services/cost_utils.py` — if the gate lands on one side only,
 * the staffing grid and the cost report disagree on worked hours.
 *
 * Two independent gates, both defaulting to "applies to everyone". A nurse is a union member iff her
 * union name is non-empty, so `""`/null/undefined all read as non-union; `cfgUnion: []` means "no
 * narrowing", not "no unions".
 */
export function unionMatchesConfig(nurseUnion: string | null | undefined, cfgIsUnion: boolean | null | undefined, cfgUnion: string[] | undefined): boolean {
	const nurseIsUnion = Boolean(nurseUnion);
	if (cfgIsUnion != null && cfgIsUnion !== nurseIsUnion) return false;
	if (cfgUnion && cfgUnion.length > 0 && !cfgUnion.includes(nurseUnion ?? "")) return false;
	return true;
}

/**
 * Union name per nurse id, for the shift-driven loops that only hold a `NurseInShift` (which has no
 * union column — resolving it there would mean a `nurse_in_shift` schema change and a backfill).
 * Built once per roster rather than a `.find()` per shift.
 */
export function buildNurseUnionMap(nurses: Pick<Nurse, "id" | "union">[] | undefined): Map<number, string> {
	return new Map((nurses ?? []).map((n) => [n.id, n.union ?? ""]));
}

/** Single-lookup counterpart to `buildNurseUnionMap` for call sites that resolve exactly one nurse. */
export function unionForNurseId(nurses: Nurse[] | undefined, nurseId: number): string {
	return nurses?.find((n) => n.id === nurseId)?.union ?? "";
}

export function computeBreakFromConfig(
	breakRules: BreakRulesConfig[],
	shiftDurationHours: number,
	role: NurseRole,
	employmentType: EmploymentType,
	nurseJobIds?: string[],
	jobManagementEnabled: boolean = false,
	nurseUnion?: string | null,
): BreakPlacement[] {
	const applicable = breakRules.filter((r) => {
		const rolesMatch = !r.roles || r.roles.length === 0 || r.roles.includes(role);
		const empMatch = !r.employmentTypes || r.employmentTypes.length === 0 || r.employmentTypes.includes(employmentType);
		const jobsMatch = !jobManagementEnabled || jobIdsMatchConfig(nurseJobIds, r.jobIds);
		const unionMatch = unionMatchesConfig(nurseUnion, r.isUnion, r.union);
		return rolesMatch && empMatch && jobsMatch && unionMatch;
	});
	applicable.sort((a, b) => a.breakLimit - b.breakLimit);

	const breaks: BreakPlacement[] = [];
	// Previously placed unpaid breaks: what has come out of work hours.
	let unpaidPlacedHours = 0;

	for (const rule of applicable) {
		const thisBreakMinutes = rule.breakTime;
		if (thisBreakMinutes <= 0) continue;
		const thisBreakHours = thisBreakMinutes / 60;
		// A paid break is worked time, so it never reduces the work hours a limit is checked against.
		const thisUnpaidHours = rule.isPaid ? 0 : thisBreakHours;

		// Override checks against raw shift duration (it will clear previous breaks)
		// Addition checks against shift minus already-placed unpaid breaks
		const remainingWork = rule.type === "override" ? shiftDurationHours - thisUnpaidHours : shiftDurationHours - unpaidPlacedHours - thisUnpaidHours;

		if (remainingWork > rule.breakLimit) {
			if (rule.type === "override") {
				breaks.length = 0;
				unpaidPlacedHours = 0;
			}
			const workOffset = rule.breakStart ?? rule.breakLimit;
			const clockOffset = workOffset + unpaidPlacedHours;
			breaks.push({ offsetHours: clockOffset, durationMinutes: thisBreakMinutes, isPaid: rule.isPaid });
			unpaidPlacedHours += thisUnpaidHours;
		}
	}

	return clearPaidBreaksOfOverlaps(breaks);
}

/**
 * Moves each paid break to just after any break it would overlap; unpaid breaks never move. Moving the paid
 * one is free (its minutes are priced as work wherever they sit); moving an unpaid one could push it past
 * the shift end. Mirrors the backend's `_clear_paid_breaks_of_overlaps`.
 *
 * Today's frontend callers only read durations, which this never changes; it is kept so placements read the
 * same on both sides (pinned by `paidBreakRules.spec.ts`), and a caller that starts using offsets gets the
 * backend's.
 */
function clearPaidBreaksOfOverlaps(breaks: BreakPlacement[]): BreakPlacement[] {
	const settled = breaks.filter((b) => !b.isPaid).map((b) => ({ start: b.offsetHours, end: b.offsetHours + b.durationMinutes / 60 }));
	return breaks.map((b) => {
		if (!b.isPaid) return b;
		const hours = b.durationMinutes / 60;
		const blockerAt = (start: number) => settled.find((i) => i.start < start + hours && start < i.end);
		let start = b.offsetHours;
		// Monotonic: each move goes past the end of an interval it overlapped, so it cannot loop.
		for (let blocker = blockerAt(start); blocker !== undefined; blocker = blockerAt(start)) start = blocker.end;
		settled.push({ start, end: start + hours });
		return start === b.offsetHours ? b : { ...b, offsetHours: start };
	});
}

export const getShiftWorkHours = (
	departmentId: string,
	endTime: DateTime,
	startTime: DateTime,
	breakRulesConfig: BreakRulesConfig[],
	role: NurseRole,
	employmentType: EmploymentType,
	nurseJobIds?: string[],
	jobManagementEnabled: boolean = false,
	nurseUnion?: string | null,
) => {
	const shiftLength = endTime.diff(startTime, "hours").as("hours");

	if (breakRulesConfig.length > 0) {
		const placements = computeBreakFromConfig(breakRulesConfig, shiftLength, role, employmentType, nurseJobIds, jobManagementEnabled, nurseUnion);
		if (placements.length > 0) {
			const unpaidBreakMinutes = placements.reduce((sum, bp) => (bp.isPaid ? sum : sum + bp.durationMinutes), 0);
			return Math.max(shiftLength - unpaidBreakMinutes / 60, 0);
		}
	}

	return shiftLength;
};

const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const satisfies readonly string[];

export function getWeekdayName<T extends number>(index: T): (typeof weekdayNames)[T] {
	return weekdayNames[index];
}

const NUMBER_OF_WEEKS_IN_CYCLE = 2;

export function getDayWeekScheduleIndex(chosenDepartment: Department, day: DateTime): number {
	const thisWeekSunday = day.startOf("week");
	const firstSunday = chosenDepartment.first_sunday.startOf("week");

	return Math.floor(thisWeekSunday.diff(firstSunday).as("weeks")) % NUMBER_OF_WEEKS_IN_CYCLE;
}

export const formatCurrency = (amount: number, fractionDigits = 2) => {
	return `$${amount.toLocaleString("en-US", {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	})}`;
};

export const safeSum = (num1: number, num2: number): number => {
	const decimal1 = new Decimal(num1);
	const decimal2 = new Decimal(num2);

	return decimal1.add(decimal2).toNumber();
};

export const safeSub = (num1: number, num2: number): number => {
	const decimal1 = new Decimal(num1);
	const decimal2 = new Decimal(num2);

	return decimal1.sub(decimal2).toNumber();
};

export const getRandomInt = (min: number, max: number) => {
	return Math.floor(Math.random() * (Math.floor(max) - Math.ceil(min) + 1)) + Math.ceil(min);
};

export const isAgencyPostedShift = (request: OpenShiftRequest | ReplaceShiftRequest): boolean => {
	const openShift = request as OpenShiftRequest;
	return Boolean(openShift.agency_source_name) || (openShift.agency_shift_ids?.length ?? 0) > 0;
};

/**
 * The response rows with a nurse on them, in the order they came — NOT deduplicated: a nurse on two rows is two rows,
 * as the Requests screen lists them. The one list every response count below is taken from, so a screen that lists the
 * responses and one that counts them always agree.
 */
export const getRespondedRequestData = (request: BaseRequest): RequestResponsesStatus[] => request.message.request_data.filter(({ nurse_id }) => !!nurse_id);

export const getRespondedNurseIds = (request: BaseRequest): number[] => getRespondedRequestData(request).map(({ nurse_id }) => nurse_id);

export const hasNurseResponse = (request: BaseRequest): boolean => getRespondedNurseIds(request).length > 0;

export const countNurseResponses = (request: BaseRequest): number => getRespondedNurseIds(request).length;

export const countRequestsWithResponses = (requests: ShiftRequest[]): number => requests.filter(hasNurseResponse).length;

/**
 * The hours a response's nurse works if approved: what they accepted when they answered for part of the shift, else
 * the posted shift's. The approve writes, the desktop applicant row's cost prediction and the gaps approve all read
 * a response's hours through this one rule.
 */
export const acceptedHoursOf = (
	request: BaseRequest,
	response: Pick<RequestResponsesStatus, "accepted_start_time" | "accepted_end_time"> | undefined,
): { start: DateTime; end: DateTime } => ({
	// DEFAULT: a response without accepted hours answered for the whole shift, so it works the posted hours. A nurse with
	// no response row on the request (`undefined`, e.g. an approval looked up by nurse) works them too, as it always has.
	start: response?.accepted_start_time ?? request.message.shift_start,
	end: response?.accepted_end_time ?? request.message.shift_end,
});

const isSameAgencySource = (candidate: OpenShiftRequest | ReplaceShiftRequest, newSourceName: string | undefined): boolean => {
	const candidateSourceName = (candidate as OpenShiftRequest).agency_source_name;
	if (!candidateSourceName || !newSourceName) return true;
	return candidateSourceName === newSourceName;
};

export const getOverlappingShift = ({
	startTime,
	endTime,
	staffRequests,
	roles,
	jobManagementEnabled,
	jobId,
	chargeOnly,
	intendedBonusWindows,
	location,
	postToAgency,
	subRole,
	agencySourceName,
}: {
	staffRequests: (OpenShiftRequest | ReplaceShiftRequest)[];
	startTime: DateTime;
	endTime: DateTime;
	roles: string[];
	jobManagementEnabled?: boolean;
	jobId?: string;
	// The windows the new/edited shift WILL have. Compare via `canMergeBonus` for snapshot safety.
	intendedBonusWindows: IncentivePlan[];
	chargeOnly: boolean | null;
	location?: string;
	postToAgency?: boolean;
	subRole?: NurseRole;
	agencySourceName?: string;
}): OpenShiftRequest | ReplaceShiftRequest | undefined => {
	return staffRequests.find(
		(request: OpenShiftRequest | ReplaceShiftRequest) =>
			request.request_type === NotificationType.OpenShift &&
			(request.charge_only ?? false) === (chargeOnly ?? false) &&
			request.message.shift_start.hasSame(startTime, "minute") &&
			request.message.shift_end.hasSame(endTime, "minute") &&
			canMergeBonus(request.incentive_windows, intendedBonusWindows) &&
			(jobManagementEnabled && jobId ? request.job_id === jobId : JSON.stringify(request.message.roles.sort()) === JSON.stringify(roles.sort())) &&
			(request.location ?? "") === (location ?? "") &&
			(!postToAgency || (isAgencyPostedShift(request) && request.sub_role === subRole && isSameAgencySource(request, agencySourceName))),
	);
};

export const roleShortStringToEnumFullRole = (shortRoleStr: string): NurseFullRole | null => {
	return (NurseFullRole as any)[shortRoleStr] ?? null;
};

export const DEPARTMENT_TYPE_TO_COLOR: Record<DepartmentType, string> = {
	[DepartmentType.Hospital]: CATEGORICAL_DEPARTMENT.Hospital,
	[DepartmentType.SNF]: CATEGORICAL_DEPARTMENT.SNF,
	[DepartmentType.ALF]: CATEGORICAL_DEPARTMENT.ALF,
};

export function hexToOpacity(hex: string, opacity: number): string {
	const clean = hex.replace("#", "");
	const r = parseInt(clean.substring(0, 2), 16);
	const g = parseInt(clean.substring(2, 4), 16);
	const b = parseInt(clean.substring(4, 6), 16);

	return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/** A stable 32-bit hash of a string: the same string always gives the same number. */
export const hashString = (str: string): number => {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash;
	}
	return hash;
};

/** Returns a deterministic integer 0–99 for any string seed (FNV-1a + avalanche finalizer). */
export const deterministicPercent = (seed: string): number => {
	let hash = 2166136261; // FNV offset basis
	for (let i = 0; i < seed.length; i++) {
		hash ^= seed.charCodeAt(i);
		hash = Math.imul(hash, 16777619); // FNV prime
	}
	// Avalanche finalizer — ensures small input changes spread across all bits
	hash ^= hash >>> 16;
	hash = Math.imul(hash, 2246822507);
	hash ^= hash >>> 13;
	return Math.abs(hash) % 100;
};

export type RoleToNursesInShiftMap = { [role: string]: NurseInShift[] };

export const groupNursesByRole = (nurses: NurseInShift[], allNurses: Nurse[]): RoleToNursesInShiftMap => {
	return nurses.reduce<RoleToNursesInShiftMap>((acc, nurse) => {
		const role = allNurses.find((n: Nurse) => n.id === nurse.nurseId)?.role || "Other";
		if (!acc[role]) {
			acc[role] = [];
		}
		acc[role].push(nurse);
		return acc;
	}, {});
};

export const shiftTypeToIndex = (shiftType: string): number => {
	return shiftType === ShiftType.Day || shiftType === ShiftType.Morning ? 0 : 1;
};

export const findDuplicateTimeSlots = (nurses: BaseNurseShift[], requests: (OpenShiftRequest | ReplaceShiftRequest)[]) => {
	const timeSlotCounts = new Map<
		string,
		{
			count: number;
			startTime: DateTime;
			endTime: DateTime;
		}
	>();

	nurses.forEach((nurse) => {
		if (nurse.isPendingToDelete) {
			return;
		}

		const startTime: DateTime = nurse.startTime.set({ minute: 0 });
		const endTime: DateTime = nurse.endTime.set({ minute: 0 });
		const key = `${startTime.toFormat("yyyy-MM-dd-HH:mm")}_${endTime.toFormat("yyyy-MM-dd-HH:mm")}`;

		if (timeSlotCounts.has(key)) {
			const current = timeSlotCounts.get(key)!;
			current.count += 1;
		} else {
			timeSlotCounts.set(key, {
				count: 1,
				startTime,
				endTime,
			});
		}
	});

	requests.forEach((request) => {
		const startTime: DateTime = request.message.shift_start.set({ minute: 0 });
		const endTime: DateTime = request.message.shift_end.set({ minute: 0 });
		const key = `${startTime.toFormat("yyyy-MM-dd-HH:mm")}_${endTime.toFormat("yyyy-MM-dd-HH:mm")}`;

		if (timeSlotCounts.has(key)) {
			const current = timeSlotCounts.get(key)!;
			current.count += request.message.roles.length;
		} else {
			timeSlotCounts.set(key, {
				count: request.message.roles.length,
				startTime,
				endTime,
			});
		}
	});

	const duplicateTimeSlots = Array.from(timeSlotCounts.values())
		.filter((slot) => slot.count > 2)
		.map((slot) => ({
			startTime: slot.startTime,
			endTime: slot.endTime,
			count: slot.count,
		}))
		.sort((a, b) => {
			const startDiff = a.startTime.valueOf() - b.startTime.valueOf();
			if (startDiff !== 0) {
				return startDiff;
			}
			return a.endTime.valueOf() - b.endTime.valueOf();
		});

	return duplicateTimeSlots;
};

export const findTimeRange = (nurses: BaseNurseShift[], requests: OpenShiftRequest[]): { earliestStartTime: DateTime; latestEndTime: DateTime } => {
	if (!nurses.length) return { earliestStartTime: DateTime.now(), latestEndTime: DateTime.now() };

	let earliestStartTime = nurses[0]?.startTime;
	let latestEndTime = nurses[0]?.endTime;

	for (let i = 1; i < nurses.length; i++) {
		const nurse = nurses[i];

		if (nurse.isPendingToDelete) {
			continue;
		}

		if (nurse.startTime < earliestStartTime) {
			earliestStartTime = nurse.startTime;
		}

		if (nurse.endTime > latestEndTime) {
			latestEndTime = nurse.endTime;
		}
	}

	for (let i = 0; i < requests.length; i++) {
		const request = requests[i];

		if (request.message.shift_start < earliestStartTime) {
			earliestStartTime = request.message.shift_start;
		}

		if (request.message.shift_end > latestEndTime) {
			latestEndTime = request.message.shift_end;
		}
	}

	return { earliestStartTime, latestEndTime };
};
const PARENTHESES_CONTENTS = /\(([^)]+)\)/;

export function getShortRole(nurseFullRole: NurseFullRole): string {
	return nurseFullRole.match(PARENTHESES_CONTENTS)?.[1] ?? nurseFullRole;
}

export function getEnumKeyByValue<T extends Record<string, string>>(enumObj: T, value: string): keyof T | undefined {
	return (Object.entries(enumObj) as [keyof T, string][]).find(([, val]) => val === value)?.[0];
}

/** One time-off row as the day-off dialogs edit it. */
export const dayOffOf = (pref: SpecificPreference): DayOff => ({
	id: pref.id,
	dateFrom: pref.start_date,
	dateTo: pref.end_date,
	hours: pref.hours,
	reasons: pref.reason ? [pref.reason] : [],
	post_shifts: pref.post_shifts,
	notes: pref.notes,
	externalTimeOff: pref.external_time_off,
});

/** Days off earliest first, then the shorter of two that start together. */
export const byDayOffDates = (dayOff: DayOff, otherDayOff: DayOff): number =>
	dayOff.dateFrom.diff(otherDayOff.dateFrom).toMillis() || dayOff.dateTo.diff(otherDayOff.dateTo).toMillis();

export function nursePreferencesToDaysOffAndPto(nurse?: Nurse): DayOff[] {
	if (nurse) {
		return nurse.employeeSpecificPreferences
			.filter((pref: SpecificPreference) => pref.type === Preferences.Avoid)
			.map(dayOffOf)
			.sort(byDayOffDates);
	}
	return [];
}

export function daysOffAndPtoToNursePreferences(daysOff: DayOff[], nurse: Nurse): SpecificPreference[] {
	return daysOff.map((dayOff) => {
		// Preserve fields the edit dialog doesn't round-trip (data, external HRIS link) from the existing preference,
		// so an optimistic edit doesn't drop the integration indicator until the next refetch.
		const existingPref = nurse.employeeSpecificPreferences.find((value) => value.type === Preferences.Avoid && value.id === dayOff.id);
		return {
			id: dayOff.id,
			type: Preferences.Avoid,
			start_date: dayOff.dateFrom,
			end_date: dayOff.dateTo,
			reason: dayOff.reasons.length > 0 ? dayOff.reasons[0] : "",
			nurse_id: nurse.id,
			hours: dayOff.hours,
			data: existingPref?.data,
			notes: dayOff.notes ?? "",
			post_shifts: dayOff.post_shifts,
			external_time_off: dayOff.externalTimeOff ?? existingPref?.external_time_off,
		};
	});
}

/**
 * Converts an enum object to an array of dropdown items.
 * @param enumObj - The enum object to convert.
 * @param labelGetter - Optional function to customize the label of each dropdown item, should be used when enum values are not strings or need special formatting.
 */
export function enumToDropdownItems<T extends Record<string, string>>(enumObj: T, labelGetter?: (val: T[keyof T]) => string): DropdownItem<keyof T>[] {
	return (Object.keys(enumObj) as Array<keyof T>).map((key) => ({
		value: key,
		label: () => (labelGetter ? labelGetter(enumObj[key]) : <>{enumObj[key]}</>),
		id: key as string,
	}));
}

/**
 * Determines the shift index based on the start and end times of a shift.
 * @param startTime
 * @param endTime
 * @return {number} - Returns 0 for day shifts (4 AM to 4 PM) and 1 for night shifts (4 PM to 4 AM).
 */
export function getShiftIndexByShiftTimes({ startTime, endTime }: { startTime: DateTime; endTime: DateTime }): number {
	/*TODO: This is a temp fix to decide between day and night - the field should be removed completely
	 * Maybe we can add a setting in dep config to define whats considered day/night shift
	 * */
	return 4 <= startTime.hour && startTime.hour < 16 ? 0 : 1;
}

/** The shift type `getShiftIndexByShiftTimes` points at in `shiftTypes`; with a single shift type, every shift is that type. */
export function getShiftTypeByShiftTimes(shiftTypes: readonly string[], times: { startTime: DateTime; endTime: DateTime }): string {
	const shiftType = shiftTypes[Math.min(getShiftIndexByShiftTimes(times), shiftTypes.length - 1)];
	if (shiftType === undefined) throw new Error("No shift types to place the shift in");
	return shiftType;
}

export function normalizeUnionName(union: string | null | undefined): string | null {
	return union?.trim() || null;
}

export function shouldApplyNurseNotOnboardedVisibleFlag(nurse?: Nurse): boolean {
	return !!nurse && !nurse.lastLoginTime && !isAgencyNurse(nurse.employmentType);
}

interface BuildNurseNotesOptions {
	includeAgency?: boolean;
	includeCharge?: boolean;
	includeNotOnboarded?: boolean;
}

export function buildNurseNotes(
	nurse: NurseInShift | null | undefined,
	hospitalData: Pick<HospitalData, "nurses">,
	{ includeAgency = true, includeCharge = true, includeNotOnboarded = true }: BuildNurseNotesOptions = {},
): string {
	if (!nurse) return "";
	let notes = nurse.notes ?? "";
	const fullNurse = hospitalData.nurses.find((n) => n.id === nurse.nurseId);
	if (nurse.trainingStatus === NurseTrainingStatus.Trainee) {
		notes = notes ? `${notes}; Trainee` : "Trainee";
	}
	if (nurse.trainingStatus === NurseTrainingStatus.Orientation) {
		notes = notes ? `${notes}; Orientation` : "Orientation";
	}
	if (nurse.trainingStatus === NurseTrainingStatus.Education) {
		notes = notes ? `${notes}; Education` : "Education";
	}
	if (includeAgency && isAgencyNurse(nurse.type)) {
		const agencyLabel = fullNurse?.agencyName || "Agency";
		notes = notes ? `${notes}; ${agencyLabel}` : agencyLabel;
	}
	if (includeCharge && nurse.charge) {
		notes = notes ? `${notes}; Shift Manager` : "Shift Manager";
	}
	if (includeNotOnboarded && shouldApplyNurseNotOnboardedVisibleFlag(fullNurse)) {
		notes += `${notes ? "; " : ""}Not Onboarded`;
	}
	return notes;
}

export function isRequestInstanceOfReplaceShiftRequest(obj: unknown): obj is ReplaceShiftRequest {
	const isReplacementNurseIdInObject = (obj: unknown): obj is ReplaceShiftRequest => {
		return (
			typeof obj === "object" &&
			obj !== null &&
			"replacement_nurse_id" in obj &&
			typeof (obj as { replacement_nurse_id: unknown }).replacement_nurse_id === "number"
		);
	};
	return isReplacementNurseIdInObject(obj) && obj.replacement_nurse_id > 0;
}

export function locationComparator(a: DepartmentLocation, b: DepartmentLocation) {
	const sectionCompare = a.section.localeCompare(b.section);
	if (sectionCompare !== 0) {
		return sectionCompare;
	}

	const aLocation = a.location_short_name || a.location;
	const bLocation = b.location_short_name || b.location;
	const locationCompare = aLocation.localeCompare(bLocation);

	if (locationCompare !== 0) {
		return locationCompare;
	}

	const shiftStartCompare = a.shift_start.localeCompare(b.shift_start);
	if (shiftStartCompare !== 0) {
		return shiftStartCompare;
	}

	return a.location.localeCompare(b.shift_end);
}

/**
 * Returns a deduplicated list of locations.
 * Uniqueness is always based on `location` name.
 * @param locations
 * @param roleUniqueness
 *   When true, `roles` are included in the uniqueness key.
 *   When false, roles are ignored for deduplication.
 * @param departmentTypeUniqueness
 *   When true, `departmentType` is included in the uniqueness key.
 *   When false, departmentType is ignored for deduplication.
 */
export function getUniqueLocations(
	locations: DepartmentLocation[],
	roleUniqueness: boolean = true,
	departmentTypeUniqueness: boolean = false,
	useJobs: boolean = false,
): DepartmentLocation[] {
	const seen = new Set<string>();

	return locations
		.map<DepartmentLocation>((value) => ({ ...value, location: value.location.trim() }))
		.filter((loc) => {
			// Normalize roles/jobs (sort + stringify) so order doesn’t matter
			const groupingKey = useJobs && loc.jobId ? JSON.stringify(loc.jobId) : JSON.stringify([...loc.roles].sort());
			const departmentTypeKey = departmentTypeUniqueness ? loc.departmentType : undefined;
			const key = `${loc.location}:_:${roleUniqueness ? groupingKey : ""}:_:${departmentTypeKey ?? ""}`;

			if (seen.has(key)) {
				return false; // duplicate
			}

			seen.add(key);
			return true;
		})
		.sort((a, b) => {
			const floorIdxA = floorToIndex(a.location);
			const floorIdxB = floorToIndex(b.location);

			if (floorIdxA !== undefined && floorIdxB !== undefined) {
				return floorIdxA - floorIdxB;
			}
			// Floor-indexed locations come before non-indexed ones
			if (floorIdxA !== undefined) return -1;
			if (floorIdxB !== undefined) return 1;

			return a.location.localeCompare(b.location);
		});
}

/**
 * The department's unit names, each once (trimmed, whatever roles it is configured for), in `getUniqueLocations`'
 * order — blank-named units included, as the classic post screen has always listed them.
 */
export const getUniqueLocationNames = (locations: DepartmentLocation[]): string[] => getUniqueLocations(locations, false).map((loc) => loc.location);

/**
 * This function should primarily be used for synergy-like locations, to avoid rendering duplicated names.
 * @param location
 */
export function facilityToString(location: DepartmentLocation | null) {
	return location
		? `${location.location} ${location.section || location.departmentType ? `(${location.section}${location.departmentType ? `,${location.departmentType}` : ""})` : ""}`
		: "";
}

const FLOOR_ALIASES: Record<string, number> = {
	"ground floor": 0,
	main: 0,
	lobby: 0,
};

const ORDINAL_WORDS: Record<string, number> = {
	first: 1,
	one: 1,
	second: 2,
	two: 2,
	third: 3,
	fourth: 4,
	fifth: 5,
	sixth: 6,
	seventh: 7,
	eighth: 8,
	ninth: 9,
	tenth: 10,
	eleventh: 11,
	twelfth: 12,
	thirteenth: 13,
	fourteenth: 14,
	fifteenth: 15,
	sixteenth: 16,
	seventeenth: 17,
	eighteenth: 18,
	nineteenth: 19,
	twentieth: 20,
};

const KEYWORD_PATTERN = /(?:floor|unit)\s*(\d+)(?:st|nd|rd|th)?|(\d+)(?:st|nd|rd|th)?\s*(?:floor|unit)/i;

/**
 * Parse a floor string into a numeric index.
 * Dynamically extracts the number from strings containing "floor" or "unit" keywords,
 * handling plain numbers ("unit 302"), ordinal suffixes ("3rd floor"), and ordinal words ("first floor").
 */
export function floorToIndex(input: string): number | undefined {
	const key = input.trim().toLowerCase();

	// 1. extract number near "floor" or "unit" keyword (highest priority)
	//    matches: "unit 302", "floor 3", "3rd floor", "unit 1st", etc.
	const keywordMatch = key.match(KEYWORD_PATTERN);
	if (keywordMatch) {
		return Number(keywordMatch[1] ?? keywordMatch[2]);
	}

	// 2. ordinal word match (e.g. "second floor", "north first unit").
	// Word-boundary match — without `\b...\b` the substring match misfires on names like
	// "Klintwor**th**" (matches "third"? no — but "klin**two**rth" matches "two"), "St**one**",
	// "**Main**waring", etc. Production data has 559 nurse names and 9 location names that hit
	// the substring trap; word-boundary keeps real cases like "north first floor" working.
	for (const [word, n] of Object.entries(ORDINAL_WORDS)) {
		if (new RegExp(`\\b${word}\\b`).test(key)) return n;
	}

	// 3. special aliases (ground floor, main, lobby) — checked last so
	//    numeric patterns like "main building 5th floor" match the number first.
	// Same word-boundary rule: "Maintenance" must not match "main", "Lobbyist" must not match "lobby".
	for (const [alias, n] of Object.entries(FLOOR_ALIASES)) {
		if (new RegExp(`\\b${alias}\\b`).test(key)) return n;
	}

	return undefined;
}
